<template>
  <div class="gd-home">
    <AppNavigation />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
        <div class="gradient-orb orb-2"></div>
      </div>

      <v-container class="hero-content py-16">
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="text-center text-md-left pr-md-8">
            <div class="hero-eyebrow mb-6" :class="{ 'is-in': mounted }">
              <v-icon size="16" color="#FFB07A">mdi-dumbbell</v-icon>
              <span>Built for gyms and fitness centers</span>
            </div>

            <h1 class="hero-title mb-6" :class="{ 'is-in': mounted }">
              The complete management system for modern fitness centers
            </h1>

            <p class="hero-subtitle mb-8" :class="{ 'is-in': mounted }">
              Streamline operations, boost member engagement, and grow your gym with an all-in-one platform designed specifically for fitness professionals.
            </p>

            <div class="cta-buttons mb-6" :class="{ 'is-in': mounted }">
              <v-btn x-large depressed class="cta-btn-primary btn-ember" @click="requestDemo">
                <v-icon left size="22" color="#0A0E14">mdi-calendar-check</v-icon>
                Request a demo
              </v-btn>
              <a href="#pricing" class="hero-link">See pricing</a>
            </div>

            <div class="trust-indicators" :class="{ 'is-in': mounted }">
              <div class="trust-badge">
                <v-icon color="white" small>mdi-lock-check</v-icon>
                <span>Secure &amp; encrypted</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-cloud-check</v-icon>
                <span>99.9% uptime</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-account-wrench</v-icon>
                <span>Done-for-you setup</span>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="text-center">
            <div class="phone-mockup-container" :class="{ 'is-in': mounted }">
              <div class="mockup-glow"></div>
              <div class="phone-frame">
                <div class="phone-screen">
                  <!-- Status Bar -->
                  <div class="status-bar">
                    <span class="time">9:41</span>
                    <div class="notch"></div>
                    <div class="status-icons">
                      <v-icon x-small color="white">mdi-signal</v-icon>
                      <v-icon x-small color="white">mdi-wifi</v-icon>
                      <v-icon x-small color="white">mdi-battery</v-icon>
                    </div>
                  </div>

                  <!-- App Header -->
                  <div class="app-header">
                    <v-icon color="white" size="20">mdi-menu</v-icon>
                    <div class="header-logo-small">
                      <v-icon color="#FF7A45" size="16">mdi-dumbbell</v-icon>
                      <span>GymDesk Suite</span>
                    </div>
                    <div class="header-actions-small">
                      <v-icon color="white" size="20">mdi-bell</v-icon>
                      <div class="notification-dot"></div>
                    </div>
                  </div>

                  <!-- Content Area -->
                  <div class="screen-content">
                    <div class="member-card">
                      <div class="member-avatar">
                        <v-icon color="#0A0E14" size="26">mdi-account</v-icon>
                      </div>
                      <div class="member-info">
                        <div class="member-name">Fitness Club</div>
                        <div class="member-status">
                          <span class="status-dot"></span>
                          <span>Active member</span>
                        </div>
                      </div>
                      <v-icon color="rgba(255,255,255,0.7)" size="26">mdi-qrcode</v-icon>
                    </div>

                    <div class="quick-actions-grid">
                      <div v-for="action in phoneActions" :key="action.label" class="action-card">
                        <div class="action-icon" :class="action.tone">
                          <v-icon :color="action.tone === 'tone-ember' ? '#0A0E14' : 'white'" size="24">{{ action.icon }}</v-icon>
                        </div>
                        <div class="action-label">{{ action.label }}</div>
                      </div>
                    </div>

                    <div class="class-card">
                      <div class="class-header">
                        <div class="class-icon">
                          <v-icon color="#D9501E" size="20">mdi-yoga</v-icon>
                        </div>
                        <div class="class-details">
                          <div class="class-title">HIIT Training</div>
                          <div class="class-time">Today at 5:00 PM &middot; Studio A</div>
                        </div>
                        <div class="class-badge">Booked</div>
                      </div>
                      <div class="class-spots">
                        <div class="spots-bar"><div class="spots-fill" style="width: 80%;"></div></div>
                        <span>12 of 15 spots</span>
                      </div>
                    </div>

                    <div class="rewards-card">
                      <div class="rewards-header">
                        <v-icon color="#FF7A45" size="20">mdi-trophy</v-icon>
                        <span class="rewards-title">Loyalty rewards</span>
                        <span class="rewards-count">7 / 10 visits</span>
                      </div>
                      <div class="rewards-track">
                        <span v-for="i in 10" :key="i" class="rewards-pip" :class="{ filled: i <= 7 }"></span>
                      </div>
                    </div>
                  </div>

                  <!-- Bottom Navigation -->
                  <div class="bottom-nav">
                    <div class="nav-item active">
                      <v-icon color="#D9501E" size="22">mdi-home</v-icon>
                      <div class="nav-indicator"></div>
                    </div>
                    <div class="nav-item"><v-icon size="22">mdi-calendar-month</v-icon></div>
                    <div class="nav-item"><v-icon size="22">mdi-dumbbell</v-icon></div>
                    <div class="nav-item"><v-icon size="22">mdi-chart-line</v-icon></div>
                    <div class="nav-item"><v-icon size="22">mdi-account-circle</v-icon></div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Features Section -->
    <section id="features" ref="features" data-section="features" class="features-section">
      <v-container>
        <div class="section-header text-center mb-12 reveal" :class="{ 'is-visible': visible.features }">
          <div class="section-badge mb-4">
            <v-icon small color="#FF7A45">mdi-star-four-points</v-icon>
            <span>One system for the whole gym</span>
          </div>
          <h2 class="section-title mb-4">Everything your gym needs</h2>
          <p class="section-subtitle">
            Comprehensive tools designed for fitness centers, so you spend less time on admin and more time with your members
          </p>
        </div>

        <v-row class="mb-4">
          <v-col
            v-for="(feature, index) in features"
            :key="index"
            cols="12"
            md="6"
            lg="4"
            class="mb-6"
          >
            <div
              class="feature-card reveal"
              :class="{ 'is-visible': visible.features }"
              :style="{ 'transition-delay': `${index * 0.07}s` }"
            >
              <div class="feature-icon" :class="feature.tone">
                <v-icon size="30" :color="feature.tone === 'tone-ember' ? '#0A0E14' : 'white'">{{ feature.icon }}</v-icon>
              </div>

              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-description">{{ feature.description }}</p>

              <div class="feature-benefits">
                <div v-for="(benefit, idx) in feature.benefits" :key="idx" class="benefit-item">
                  <v-icon size="14" color="#FFB07A">mdi-check-circle</v-icon>
                  <span>{{ benefit }}</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Platform Section -->
    <section id="platform" data-section="platform" class="platform-section">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" order="2" order-md="1">
            <div class="platform-interface reveal from-left" :class="{ 'is-visible': visible.platform }">
              <div class="platform-tabs" role="tablist">
                <button
                  v-for="(tab, index) in platformTabs"
                  :key="index"
                  type="button"
                  role="tab"
                  class="platform-tab"
                  :class="{ active: activeTab === index }"
                  :aria-selected="activeTab === index ? 'true' : 'false'"
                  @click="activeTab = index"
                >
                  <v-icon size="20">{{ tab.icon }}</v-icon>
                  <span>{{ tab.label }}</span>
                </button>
              </div>

              <div class="platform-content">
                <transition name="fade" mode="out-in">
                  <div :key="activeTab" class="platform-panel">
                    <div class="platform-visual" :class="platformTabs[activeTab].tone">
                      <v-icon size="72" :color="platformTabs[activeTab].tone === 'tone-ember' ? '#0A0E14' : 'white'">
                        {{ platformTabs[activeTab].icon }}
                      </v-icon>
                    </div>
                    <h4 class="platform-panel-title">{{ platformTabs[activeTab].title }}</h4>
                    <p class="platform-panel-text">{{ platformTabs[activeTab].description }}</p>

                    <div class="platform-list">
                      <div v-for="(item, idx) in platformTabs[activeTab].items" :key="idx" class="platform-list-item">
                        <div class="list-check">
                          <v-icon size="14" color="#0A0E14">mdi-check-bold</v-icon>
                        </div>
                        <div class="list-text">{{ item }}</div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6" order="1" order-md="2">
            <div class="platform-info reveal from-right" :class="{ 'is-visible': visible.platform }">
              <div class="section-badge mb-4">
                <v-icon small color="#FF7A45">mdi-creation</v-icon>
                <span>Made for everyone in the building</span>
              </div>

              <h2 class="section-title mb-4">Designed around how a gym actually runs</h2>

              <p class="section-text mb-8">
                Members, trainers, front desk, and owners each get what they need from the same system, so nothing falls between a spreadsheet and a sticky note.
              </p>

              <div class="info-grid">
                <div v-for="(fact, index) in platformFacts" :key="index" class="info-tile">
                  <div class="info-icon" :class="fact.tone">
                    <v-icon :color="fact.tone === 'tone-ember' ? '#0A0E14' : 'white'" size="22">{{ fact.icon }}</v-icon>
                  </div>
                  <div class="info-details">
                    <div class="info-value">{{ fact.value }}</div>
                    <div class="info-label">{{ fact.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits Section -->
    <section data-section="benefits" class="benefits-section">
      <div class="benefits-background">
        <div class="benefit-shape shape-1"></div>
        <div class="benefit-shape shape-2"></div>
      </div>

      <v-container class="benefits-container">
        <div class="section-header text-center mb-12 reveal" :class="{ 'is-visible': visible.benefits }">
          <div class="section-badge mb-4">
            <v-icon small color="#FF7A45">mdi-shield-star</v-icon>
            <span>Why gyms choose GymDesk Suite</span>
          </div>
          <h2 class="section-title mb-4">Built to grow your fitness business</h2>
          <p class="section-subtitle">
            More than software &mdash; a complete solution that changes how you run your gym
          </p>
        </div>

        <v-row>
          <v-col
            v-for="(benefit, index) in coreBenefits"
            :key="index"
            cols="12"
            md="6"
            class="mb-6"
          >
            <div
              class="benefit-card reveal"
              :class="{ 'is-visible': visible.benefits }"
              :style="{ 'transition-delay': `${index * 0.1}s` }"
            >
              <div class="benefit-icon-section">
                <div class="benefit-icon-wrap" :class="benefit.tone">
                  <v-icon size="34" :color="benefit.tone === 'tone-ember' ? '#0A0E14' : 'white'">{{ benefit.icon }}</v-icon>
                </div>
                <div class="benefit-badge">{{ benefit.badge }}</div>
              </div>

              <h3 class="benefit-title">{{ benefit.title }}</h3>
              <p class="benefit-text">{{ benefit.description }}</p>

              <div class="benefit-points">
                <div v-for="(point, idx) in benefit.points" :key="idx" class="point-item">
                  <div class="point-dot"></div>
                  <span>{{ point }}</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Tribzz Section -->
    <section class="tribzz-section">
      <v-container>
        <div class="tribzz-band">
          <div class="tribzz-copy">
            <div class="section-badge mb-4"><v-icon small color="#FF7A45">mdi-account-group-outline</v-icon><span>New · Tribzz</span></div>
            <h2 class="section-title tribzz-title mb-4">Give your members a place to gather</h2>
            <p class="tribzz-text">
              Tribzz is the social platform from the team behind GymDesk Suite. Start a free Tribzz for your gym to post
              the week's classes, run challenges and see who's training right now. No ads, ever.
            </p>
            <ul class="tribzz-points">
              <li><v-icon size="18" color="#FF7A45">mdi-check-bold</v-icon><span>Class times and open spots your members actually see</span></li>
              <li><v-icon size="18" color="#FF7A45">mdi-check-bold</v-icon><span>Check-ins that show who's here now</span></li>
              <li><v-icon size="18" color="#FF7A45">mdi-check-bold</v-icon><span>Challenges, polls and events that keep people coming back</span></li>
            </ul>
            <div class="tribzz-actions">
              <v-btn large depressed class="btn-ember" to="/tribzz">See how gyms use it</v-btn>
              <a :href="tribzzUrl" target="_blank" rel="noopener" class="tribzz-link">Go to Tribzz <v-icon size="16" color="#FFB07A">mdi-open-in-new</v-icon></a>
            </div>
          </div>
          <div class="tribzz-mock" aria-hidden="true">
            <div class="tm-card">
              <div class="tm-cover"><span>I</span></div>
              <div class="tm-body">
                <div class="tm-title">Iron Forge Fitness</div>
                <div class="tm-sub">Classes, challenges and who's training now.</div>
                <div class="tm-chips"><span>Appointments</span><span>Here now</span><span>Polls</span></div>
                <div class="tm-post">&ldquo;3 spots left in the 6pm class 🏋&rdquo;</div>
                <div class="tm-join">Join</div>
              </div>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" data-section="pricing" class="pricing-section">
      <v-container>
        <div class="section-header text-center mb-12 reveal" :class="{ 'is-visible': visible.pricing }">
          <div class="section-badge mb-4">
            <v-icon small color="#FF7A45">mdi-currency-usd</v-icon>
            <span>Transparent pricing</span>
          </div>
          <h2 class="section-title mb-4">Simple pricing for gyms of all sizes</h2>
          <p class="section-subtitle">
            One monthly price with every feature included, plus a one-time onboarding fee. No hidden fees or surprises.
          </p>
        </div>

        <v-row justify="center">
          <v-col cols="12" xl="11">
            <div class="pricing-card reveal scale" :class="{ 'is-visible': visible.pricing }">
              <div class="pricing-badge">
                <v-icon small color="#0A0E14">mdi-star</v-icon>
                <span>All features</span>
              </div>

              <div class="pricing-layout">
                <!-- Summary column: price + CTA -->
                <div class="pricing-summary">
                  <div class="pricing-icon">
                    <v-icon size="30" color="#0A0E14">mdi-dumbbell</v-icon>
                  </div>
                  <h3 class="pricing-plan-name">GymDesk Suite Platform</h3>
                  <p class="pricing-tagline">Everything your gym needs to thrive</p>

                  <div class="pricing-amount-container">
                    <div class="pricing-amount-large">
                      <span class="currency">$</span>
                      <span class="amount">199<span class="cents">.99</span></span>
                    </div>
                    <div class="pricing-period-label">Per month</div>
                    <p class="pricing-onboarding">+ $499 one-time onboarding &amp; setup</p>
                    <p class="pricing-note">Billed monthly &bull; No long-term contract &bull; Cancel anytime</p>
                  </div>

                  <v-btn
                    x-large
                    block
                    depressed
                    class="mb-4 btn-ember pricing-btn"
                    @click="requestDemo"
                  >
                    <v-icon left size="22" color="#0A0E14">mdi-calendar-check</v-icon>
                    Request a demo
                  </v-btn>

                  <div class="pricing-guarantees">
                    <div class="guarantee-item">
                      <v-icon small color="#FFB07A">mdi-check-decagram</v-icon>
                      <span>30-day money-back guarantee</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#FFB07A">mdi-check-decagram</v-icon>
                      <span>Done-for-you setup &amp; member import</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#FFB07A">mdi-check-decagram</v-icon>
                      <span>No long-term contracts</span>
                    </div>
                  </div>
                </div>

                <!-- Features column: compact multi-column grid -->
                <div class="pricing-features-panel">
                  <div class="feature-groups">
                    <div v-for="(group, index) in featureGroups" :key="index" class="feature-group">
                      <div class="group-header">
                        <v-icon color="#FF7A45" size="18">{{ group.icon }}</v-icon>
                        <h4>{{ group.title }}</h4>
                      </div>
                      <div class="group-features">
                        <div
                          v-for="(feature, idx) in group.features"
                          :key="idx"
                          class="feature-row"
                          :title="feature.detail || ''"
                        >
                          <v-icon color="#FFB07A" size="16">mdi-check-circle</v-icon>
                          <span class="feature-name">{{ feature.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="final-cta-section">
      <div class="cta-background">
        <div class="cta-orb cta-orb-1"></div>
        <div class="cta-orb cta-orb-2"></div>
      </div>

      <v-container class="text-center">
        <div class="cta-content">
          <div class="cta-mark mb-6">
            <v-icon size="34" color="#0A0E14">mdi-dumbbell</v-icon>
          </div>
          <h2 class="cta-title mb-4">Ready to transform your gym?</h2>
          <p class="cta-subtitle mb-8">
            See GymDesk Suite set up for your classes, your memberships, and your members, with the setup done for you.
          </p>

          <div class="cta-buttons-final">
            <v-btn x-large depressed class="btn-ember cta-btn-final" @click="requestDemo">
              <v-icon left size="22" color="#0A0E14">mdi-calendar-check</v-icon>
              Request a demo
            </v-btn>
          </div>
        </div>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
  import AppNavigation from '@/components/AppNavigation.vue'
  import AppFooter from '@/components/AppFooter.vue'
  import { TRIBZZ_URL } from '@/utils/tribzz'

  export default {
    name: 'GymDeskHome',
    components: { AppNavigation, AppFooter },

    data: () => ({
      tribzzUrl: TRIBZZ_URL,
      mounted: false,
      visible: {
        features: false,
        platform: false,
        benefits: false,
        pricing: false
      },
      activeTab: 0,
      observer: null,

      phoneActions: [
        { icon: 'mdi-silverware-fork-knife', label: 'Meal Plans', tone: 'tone-ember' },
        { icon: 'mdi-calendar-clock', label: 'Classes', tone: 'tone-steel' },
        { icon: 'mdi-account-supervisor', label: 'Booking', tone: 'tone-iron' },
        { icon: 'mdi-card-account-details', label: 'Membership', tone: 'tone-copper' }
      ],

      features: [
        {
          icon: 'mdi-silverware-fork-knife',
          tone: 'tone-ember',
          title: 'Custom Meal Plans',
          description: 'Create and assign personalized nutrition plans for your clients. Track their progress and help them achieve their fitness goals with tailored dietary guidance.',
          benefits: [
            'Pre-built templates for various fitness goals',
            'Macro and calorie tracking integration',
            'Easy-to-share digital meal plans'
          ]
        },
        {
          icon: 'mdi-calendar-clock',
          tone: 'tone-steel',
          title: 'Automated Class Booking',
          description: 'Let members reserve spots in classes and personal training sessions right from their phones. No more phone calls or manual scheduling.',
          benefits: [
            'Real-time availability updates',
            'Automated waitlist management',
            'Class capacity control'
          ]
        },
        {
          icon: 'mdi-card-account-details',
          tone: 'tone-copper',
          title: 'Membership Management',
          description: 'Track renewals, payments, and upgrades in one simple system. Automated billing and renewal reminders keep your revenue flowing smoothly.',
          benefits: [
            'Automatic payment processing',
            'Flexible membership tiers',
            'Renewal reminder notifications'
          ]
        },
        {
          icon: 'mdi-bell-ring',
          tone: 'tone-iron',
          title: 'Push Notifications',
          description: 'Keep members engaged with timely class updates, new program announcements, and special promotions delivered straight to their devices.',
          benefits: [
            'Schedule notifications in advance',
            'Targeted messaging by member type',
            'Track open and engagement rates'
          ]
        },
        {
          icon: 'mdi-trophy',
          tone: 'tone-ember',
          title: 'Loyalty Rewards',
          description: 'Encourage retention with built-in loyalty programs. Reward consistent attendance, referrals, and milestone achievements to keep members motivated.',
          benefits: [
            'Customizable reward tiers',
            'Automated point tracking',
            'Referral program management'
          ]
        },
        {
          icon: 'mdi-account-heart',
          tone: 'tone-steel',
          title: 'Enhanced Member Experience',
          description: 'Offer personalized fitness profiles, complete training history, and seamless communication with staff. Make every member feel valued.',
          benefits: [
            'Digital fitness profiles & progress tracking',
            'In-app messaging with trainers',
            'Workout history and analytics'
          ]
        }
      ],

      platformTabs: [
        {
          icon: 'mdi-cellphone',
          label: 'Members',
          tone: 'tone-ember',
          title: 'The app members open before every workout',
          description: 'Booking, check-in, nutrition, and progress, all in their pocket.',
          items: [
            'Book classes and personal training from their phone',
            'Contactless mobile check-in',
            'Meal plans, workout history, and progress photos',
            'Points, achievement badges, and leaderboards'
          ]
        },
        {
          icon: 'mdi-whistle',
          label: 'Trainers',
          tone: 'tone-steel',
          title: 'Less admin between sessions',
          description: 'Everything a trainer needs to know about a client, in one profile.',
          items: [
            'Build and assign custom meal plans',
            'See each client\'s goals, measurements, and history',
            'Message clients directly in the app',
            'Recurring bookings for regulars'
          ]
        },
        {
          icon: 'mdi-monitor-dashboard',
          label: 'Front desk',
          tone: 'tone-copper',
          title: 'A calmer front desk',
          description: 'Self-service booking takes the phone calls and paperwork off your staff.',
          items: [
            'Waitlists fill cancellations automatically',
            'Class capacity managed so nothing overbooks',
            'Digital agreements and signatures',
            'Automatic reminders reduce no-shows'
          ]
        },
        {
          icon: 'mdi-chart-line',
          label: 'Owners',
          tone: 'tone-iron',
          title: 'The business side, handled',
          description: 'Revenue keeps moving while you focus on the floor.',
          items: [
            'Automated recurring billing',
            'Renewal reminders and easy tier upgrades',
            'Payment analytics for revenue and trends',
            'Push, SMS, and email campaigns'
          ]
        }
      ],

      platformFacts: [
        { icon: 'mdi-cellphone-link', value: 'iOS & Android', label: 'Native member apps', tone: 'tone-ember' },
        { icon: 'mdi-cloud-check', value: 'Cloud-based', label: 'Accessible anywhere', tone: 'tone-steel' },
        { icon: 'mdi-palette-swatch', value: 'Your brand', label: 'Your logo on the app', tone: 'tone-copper' },
        { icon: 'mdi-update', value: 'Always current', label: 'Regular feature updates', tone: 'tone-iron' }
      ],

      coreBenefits: [
        {
          icon: 'mdi-clock-fast',
          tone: 'tone-ember',
          badge: 'Save time',
          title: 'Streamline Operations',
          description: 'Automate repetitive tasks and reduce administrative burden. Spend less time on paperwork and more time with your members.',
          points: [
            'Automated billing and payment processing',
            'Self-service booking reduces staff workload',
            'Digital check-ins and attendance tracking',
            'Automated class reminders and confirmations'
          ]
        },
        {
          icon: 'mdi-chart-line',
          tone: 'tone-steel',
          badge: 'Boost revenue',
          title: 'Increase Membership Sales',
          description: 'Convert more leads with professional digital experiences. Automated marketing and seamless onboarding help you grow faster.',
          points: [
            'Professional mobile app attracts new members',
            'Automated follow-ups improve conversion',
            'Upsell opportunities through the platform',
            'Referral programs drive word-of-mouth growth'
          ]
        },
        {
          icon: 'mdi-account-heart-outline',
          tone: 'tone-copper',
          badge: 'Improve retention',
          title: 'Keep Members Engaged',
          description: 'Happy members stay longer. Use rewards, personalization, and seamless experiences to build lasting relationships.',
          points: [
            'Loyalty rewards encourage consistent attendance',
            'Push notifications keep members connected',
            'Personalized meal plans show you care',
            'Easy communication builds relationships'
          ]
        },
        {
          icon: 'mdi-devices',
          tone: 'tone-iron',
          badge: 'Modern technology',
          title: 'Stay Competitive',
          description: 'Stand out from traditional gyms with modern technology. Members expect digital experiences — give them what they want.',
          points: [
            'Native iOS and Android apps',
            'Cloud-based system accessible anywhere',
            'Regular updates with new features',
            'Professional branding with your logo'
          ]
        }
      ],

      featureGroups: [
        {
          icon: 'mdi-silverware-fork-knife',
          title: 'Nutrition & Meal Planning',
          features: [
            { name: 'Custom meal plan creator', detail: 'Build personalized plans for each client' },
            { name: 'Meal templates library', detail: 'Pre-built plans for common goals' },
            { name: 'Macro & calorie tracking', detail: 'Help clients hit their targets' },
            { name: 'Recipe database', detail: 'Hundreds of healthy recipes included' },
            { name: 'Shopping list generator', detail: 'Make grocery shopping easier' }
          ]
        },
        {
          icon: 'mdi-calendar-check',
          title: 'Class & Session Booking',
          features: [
            { name: 'Online class scheduling', detail: 'Members book from their phones' },
            { name: 'Personal training booking', detail: 'One-on-one session scheduling' },
            { name: 'Automated waitlists', detail: 'Fill cancellations automatically' },
            { name: 'Class capacity management', detail: 'Never overbook classes' },
            { name: 'Recurring bookings', detail: 'Set it and forget it for regulars' }
          ]
        },
        {
          icon: 'mdi-card-account-details',
          title: 'Membership Management',
          features: [
            { name: 'Automated billing', detail: 'Recurring payment processing' },
            { name: 'Multiple membership tiers', detail: 'Unlimited membership options' },
            { name: 'Contract management', detail: 'Digital agreements and signatures' },
            { name: 'Renewal reminders', detail: 'Automatic notification system' },
            { name: 'Upgrade management', detail: 'Easy tier upgrades for members' },
            { name: 'Payment analytics', detail: 'Track revenue and trends' }
          ]
        },
        {
          icon: 'mdi-bell-ring',
          title: 'Communication & Notifications',
          features: [
            { name: 'Push notifications', detail: 'Instant alerts to member devices' },
            { name: 'SMS notifications', detail: 'Reach members via text message' },
            { name: 'Email campaigns', detail: 'Automated marketing emails' },
            { name: 'In-app messaging', detail: 'Direct chat with staff' },
            { name: 'Class reminders', detail: 'Reduce no-shows automatically' }
          ]
        },
        {
          icon: 'mdi-trophy',
          title: 'Loyalty & Rewards',
          features: [
            { name: 'Points system', detail: 'Reward attendance and milestones' },
            { name: 'Referral program', detail: 'Incentivize member referrals' },
            { name: 'Achievement badges', detail: 'Gamify the fitness journey' },
            { name: 'Reward redemption', detail: 'Members redeem for perks' },
            { name: 'Leaderboards', detail: 'Foster friendly competition' }
          ]
        },
        {
          icon: 'mdi-account-box',
          title: 'Member Experience',
          features: [
            { name: 'Personalized profiles', detail: 'Track goals, preferences, and progress' },
            { name: 'Workout history', detail: 'Complete training log' },
            { name: 'Progress photos', detail: 'Visual transformation tracking' },
            { name: 'Body measurements', detail: 'Track weight, body fat, etc.' },
            { name: 'Staff directory', detail: 'Connect with trainers and staff' },
            { name: 'Mobile check-in', detail: 'Contactless entry system' }
          ]
        }
      ]
    }),

    mounted() {
      setTimeout(() => {
        this.mounted = true;
      }, 100);

      this.setupScrollAnimations();

      // Older links used /?demo=1 to open a demo dialog here; send them to the demo page
      if (this.$route && this.$route.query.demo) {
        this.$router.replace('/demo').catch(() => {});
      }
    },

    beforeDestroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },

    methods: {
      requestDemo() {
        this.$router.push('/demo').catch(() => {});
      },

      setupScrollAnimations() {
        // No observer support: show everything rather than leave sections hidden
        if (typeof IntersectionObserver === 'undefined') {
          Object.keys(this.visible).forEach(key => { this.visible[key] = true; });
          return;
        }

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const name = entry.target.getAttribute('data-section');
              if (name in this.visible) {
                this.visible[name] = true;
                this.observer.unobserve(entry.target);
              }
            }
          });
        }, { threshold: 0.12, rootMargin: '0px 0px -50px 0px' });

        this.$nextTick(() => {
          this.$el.querySelectorAll('[data-section]').forEach(section => this.observer.observe(section));
        });
      }
    }
  };
</script>

<style scoped>

  * {
    box-sizing: border-box;
  }

  /* ==================== THEME TOKENS ==================== */
  .gd-home {
    --bg-page: #0A0E14;
    --bg-alt: #0F141C;
    --bg-card: #151B25;
    --bg-card-hover: #1A2230;
    --border-subtle: rgba(226, 232, 240, 0.09);
    --border-ember: rgba(255, 122, 69, 0.38);
    --text-primary: #F1F3F6;
    --text-secondary: rgba(241, 243, 246, 0.7);
    --text-muted: rgba(241, 243, 246, 0.5);
    --ember: #FF7A45;
    --ember-light: #FFB07A;
    --ember-dark: #D9501E;
    --ink: #0A0E14;
    --font-display: 'Fraunces', Georgia, serif;
    --font-body: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;

    font-family: var(--font-body);
    color: var(--text-primary);
  }

  .gd-home {
    overflow-x: hidden;
    background: var(--bg-page);
    padding-top: 72px;
  }

  .gd-home section[id] {
    scroll-margin-top: 72px;
  }

  .gd-home h1,
  .gd-home h2,
  .gd-home h3,
  .gd-home h4 {
    font-family: var(--font-display);
    font-optical-sizing: auto;
  }

  /* Icon tile tones, shared by every section */
  .tone-ember  { background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 100%); }
  .tone-copper { background: linear-gradient(140deg, #E8703C 0%, #A83C14 100%); }
  .tone-steel  { background: linear-gradient(140deg, #5F7088 0%, #33415A 100%); }
  .tone-iron   { background: linear-gradient(140deg, #3A4558 0%, #1E2633 100%); }

  /* Unified ember button, used across hero, pricing, dialog, CTA */
  .btn-ember {
    text-transform: none !important;
    font-family: var(--font-body);
    font-weight: 600 !important;
    letter-spacing: 0.01em;
    font-size: 15px;
    border-radius: 12px !important;
    background: linear-gradient(150deg, #FFB07A 0%, #FF7A45 55%, #D9501E 100%) !important;
    color: #0A0E14 !important;
    box-shadow: 0 6px 20px rgba(255, 122, 69, 0.26) !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .btn-ember:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 26px rgba(255, 122, 69, 0.36) !important;
  }

  .btn-ember.v-btn.v-btn.v-btn--disabled.v-btn--has-bg {
    color: #0A0E14 !important;
    opacity: 0.4;
    box-shadow: none !important;
  }

  .gd-home a:focus-visible,
  .gd-home button:focus-visible {
    outline: 2px solid var(--ember-light);
    outline-offset: 3px;
  }

  /* Scroll reveal: one shared pattern */
  .reveal {
    opacity: 0;
    transform: translateY(22px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.25s ease, background 0.25s ease;
  }

  .reveal.from-left  { transform: translateX(-40px); }
  .reveal.from-right { transform: translateX(40px); }
  .reveal.scale      { transform: scale(0.96); }

  .reveal.is-visible {
    opacity: 1;
    transform: none;
  }

  /* ==================== HERO SECTION ==================== */
  .hero-section {
    position: relative;
    min-height: 92vh;
    background: linear-gradient(165deg, #131A25 0%, #0A0E14 55%, #07090D 100%);
    display: flex;
    align-items: center;
    overflow: hidden;
    padding-top: 48px;
  }

  .hero-background {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .gradient-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
  }

  .orb-1 {
    width: 580px;
    height: 580px;
    background: radial-gradient(circle, rgba(255, 122, 69, 0.42) 0%, transparent 70%);
    top: -200px;
    right: -160px;
    opacity: 0.38;
  }

  .orb-2 {
    width: 460px;
    height: 460px;
    background: radial-gradient(circle, rgba(95, 112, 136, 0.5) 0%, transparent 70%);
    bottom: -220px;
    left: -140px;
    opacity: 0.3;
  }

  .hero-content {
    position: relative;
    z-index: 2;
  }

  /* Page-load sequence: the one orchestrated moment */
  .hero-eyebrow,
  .hero-title,
  .hero-subtitle,
  .cta-buttons,
  .trust-indicators,
  .phone-mockup-container {
    opacity: 0;
    transition: opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .hero-eyebrow      { transform: translateY(-8px); }
  .hero-title        { transform: translateX(-24px); transition-delay: 0.15s; }
  .hero-subtitle     { transform: translateX(-24px); transition-delay: 0.3s; }
  .cta-buttons       { transform: translateY(18px);  transition-delay: 0.45s; }
  .trust-indicators  { transition-delay: 0.6s; }
  .phone-mockup-container { transform: translateY(44px); transition-delay: 0.3s; transition-duration: 1s; }

  .hero-eyebrow.is-in,
  .hero-title.is-in,
  .hero-subtitle.is-in,
  .cta-buttons.is-in,
  .trust-indicators.is-in,
  .phone-mockup-container.is-in {
    opacity: 1;
    transform: none;
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.85);
  }

  .hero-title {
    font-size: 3.5rem;
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -0.022em;
    color: var(--text-primary);
  }

  .hero-subtitle {
    font-size: 1.2rem;
    line-height: 1.65;
    font-weight: 400;
    color: var(--text-secondary);
    max-width: 34em;
  }

  .cta-buttons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 28px;
  }

  .cta-btn-primary {
    font-size: 16px;
    padding: 26px 34px !important;
  }

  .hero-link {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 15px;
    text-decoration: none;
    padding-bottom: 3px;
    border-bottom: 1px solid var(--border-ember);
    transition: color 0.2s ease, border-color 0.2s ease;
  }

  .hero-link:hover {
    color: var(--ember-light);
    border-color: var(--ember-light);
  }

  .trust-indicators {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 30px;
  }

  .trust-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.78);
  }

  /* Phone Mockup */
  .phone-mockup-container {
    position: relative;
    display: inline-block;
  }

  .mockup-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 110%;
    background: radial-gradient(circle, rgba(255, 122, 69, 0.24) 0%, transparent 68%);
    filter: blur(44px);
  }

  .phone-frame {
    position: relative;
    width: 340px;
    height: 690px;
    background: #161A20;
    border-radius: 46px;
    padding: 12px;
    box-shadow:
      0 0 0 1px #2B323D,
      0 0 0 8px #11151B,
      0 0 0 9px #2B323D,
      0 40px 90px rgba(0, 0, 0, 0.6);
  }

  .phone-screen {
    width: 100%;
    height: 100%;
    background: #F3F4F6;
    border-radius: 35px;
    overflow: hidden;
    position: relative;
    text-align: left;
    font-family: var(--font-body);
  }

  .status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    background: #121823;
    color: white;
    font-size: 13px;
    font-weight: 600;
    position: relative;
  }

  .notch {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 130px;
    height: 22px;
    background: #161A20;
    border-radius: 0 0 18px 18px;
  }

  .status-icons {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px 14px;
    background: #121823;
    color: white;
  }

  .header-logo-small {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 600;
  }

  .header-actions-small {
    position: relative;
  }

  .notification-dot {
    position: absolute;
    top: -1px;
    right: -1px;
    width: 9px;
    height: 9px;
    background: #FF7A45;
    border-radius: 50%;
    border: 2px solid #121823;
  }

  .screen-content {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .member-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border-radius: 16px;
    background: linear-gradient(140deg, #1E2633 0%, #0F141C 100%);
    color: white;
  }

  .member-avatar {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 100%);
    flex-shrink: 0;
  }

  .member-info {
    flex: 1;
  }

  .member-name {
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
  }

  .member-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 3px;
  }

  .status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4ADE80;
  }

  .quick-actions-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .action-card {
    background: white;
    border-radius: 14px;
    padding: 14px 10px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    box-shadow: 0 1px 3px rgba(15, 20, 28, 0.08);
  }

  .action-icon {
    width: 46px;
    height: 46px;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-label {
    font-size: 12.5px;
    font-weight: 600;
    color: #1E2633;
  }

  .class-card,
  .rewards-card {
    background: white;
    border-radius: 14px;
    padding: 14px;
    box-shadow: 0 1px 3px rgba(15, 20, 28, 0.08);
  }

  .class-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .class-icon {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: rgba(255, 122, 69, 0.14);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .class-details {
    flex: 1;
    min-width: 0;
  }

  .class-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #1E2633;
  }

  .class-time {
    font-size: 11.5px;
    color: #667085;
    margin-top: 2px;
  }

  .class-badge {
    font-size: 11px;
    font-weight: 600;
    color: #18794E;
    background: #DDF3E4;
    padding: 4px 10px;
    border-radius: 999px;
  }

  .class-spots {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    font-size: 11.5px;
    color: #667085;
    white-space: nowrap;
  }

  .spots-bar {
    flex: 1;
    height: 5px;
    border-radius: 3px;
    background: #E6E8EC;
    overflow: hidden;
  }

  .spots-fill {
    height: 100%;
    border-radius: 3px;
    background: #33415A;
  }

  .rewards-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }

  .rewards-title {
    flex: 1;
    font-size: 13.5px;
    font-weight: 700;
    color: #1E2633;
  }

  .rewards-count {
    font-size: 11.5px;
    font-weight: 600;
    color: #667085;
  }

  .rewards-track {
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    gap: 4px;
  }

  .rewards-pip {
    height: 7px;
    border-radius: 4px;
    background: #E6E8EC;
  }

  .rewards-pip.filled {
    background: linear-gradient(90deg, #FFB07A 0%, #FF7A45 100%);
  }

  .bottom-nav {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 12px 20px 18px;
    background: white;
    border-top: 1px solid #E6E8EC;
  }

  .nav-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #667085;
  }

  .nav-indicator {
    width: 5px;
    height: 5px;
    background: #D9501E;
    border-radius: 50%;
    position: absolute;
    bottom: -8px;
  }

  /* ==================== SHARED SECTION HEADERS ==================== */
  .section-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    background: rgba(255, 122, 69, 0.1);
    border: 1px solid var(--border-ember);
    border-radius: 30px;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--ember-light);
  }

  .section-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text-primary);
    line-height: 1.12;
  }

  .section-subtitle {
    font-size: 1.15rem;
    line-height: 1.6;
    color: var(--text-secondary);
    max-width: 680px;
    margin: 0 auto;
  }

  .section-text {
    font-size: 1.1rem;
    color: var(--text-secondary);
    line-height: 1.75;
  }

  /* ==================== FEATURES SECTION ==================== */
  .features-section {
    padding: 100px 0 64px;
    background: var(--bg-page);
  }

  .feature-card {
    background: var(--bg-card);
    border-radius: 20px;
    padding: 38px 32px;
    height: 100%;
    border: 1px solid var(--border-subtle);
  }

  .feature-card:hover {
    background: var(--bg-card-hover);
    border-color: var(--border-ember);
  }

  .feature-icon {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  }

  .feature-title {
    font-size: 1.5rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.25;
  }

  .feature-description {
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.65;
    margin-bottom: 20px;
  }

  .feature-benefits {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid var(--border-subtle);
  }

  .benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.5;
  }

  .benefit-item .v-icon {
    margin-top: 4px;
  }

  /* ==================== PLATFORM SECTION ==================== */
  .platform-section {
    padding: 100px 0;
    background: var(--bg-alt);
    border-top: 1px solid var(--border-subtle);
    border-bottom: 1px solid var(--border-subtle);
  }

  .platform-interface {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  }

  .platform-tabs {
    display: flex;
    background: rgba(255, 255, 255, 0.03);
    padding: 8px;
    gap: 6px;
    border-bottom: 1px solid var(--border-subtle);
  }

  .platform-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 10px;
    background: transparent;
    border: 0;
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .platform-tab ::v-deep .v-icon {
    color: currentColor;
  }

  .platform-tab:hover {
    background: rgba(255, 122, 69, 0.08);
    color: var(--ember-light);
  }

  .platform-tab.active {
    background: rgba(255, 122, 69, 0.14);
    color: var(--ember-light);
  }

  .platform-content {
    padding: 36px;
  }

  .platform-visual {
    height: 170px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 26px;
  }

  .platform-panel-title {
    font-size: 1.7rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
    color: var(--text-primary);
    margin-bottom: 10px;
  }

  .platform-panel-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .platform-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .platform-list-item {
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }

  .list-check {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin-top: 1px;
    background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .list-text {
    flex: 1;
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.55;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .info-tile {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 20px;
    background: var(--bg-card);
    border-radius: 16px;
    border: 1px solid var(--border-subtle);
  }

  .info-icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info-value {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.15;
    margin-bottom: 3px;
  }

  .info-label {
    font-size: 0.9rem;
    color: var(--text-secondary);
  }

  /* ==================== BENEFITS SECTION ==================== */
  .benefits-section {
    position: relative;
    padding: 100px 0 76px;
    background: linear-gradient(165deg, #0F141C 0%, #0A0E14 100%);
    overflow: hidden;
  }

  .benefits-background {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .benefits-container {
    position: relative;
    z-index: 1;
  }

  .benefit-shape {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
  }

  .shape-1 {
    width: 500px;
    height: 500px;
    top: -120px;
    left: -120px;
    background: rgba(255, 122, 69, 0.07);
  }

  .shape-2 {
    width: 440px;
    height: 440px;
    bottom: -120px;
    right: -120px;
    background: rgba(95, 112, 136, 0.12);
  }

  .benefit-card {
    height: 100%;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 20px;
    padding: 36px;
  }

  .benefit-card:hover {
    border-color: var(--border-ember);
  }

  .benefit-icon-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  .benefit-icon-wrap {
    width: 68px;
    height: 68px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  }

  .benefit-badge {
    padding: 6px 16px;
    background: rgba(255, 122, 69, 0.1);
    border: 1px solid var(--border-ember);
    color: var(--ember-light);
    border-radius: 20px;
    font-size: 12.5px;
    font-weight: 600;
  }

  .benefit-title {
    font-size: 1.6rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.25;
  }

  .benefit-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 20px;
  }

  .benefit-points {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .point-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 0.97rem;
    color: var(--text-secondary);
    line-height: 1.6;
  }

  .point-dot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    background: var(--ember);
    border-radius: 50%;
    margin-top: 9px;
  }

  /* ==================== PRICING SECTION ==================== */
  .pricing-section {
    padding: 100px 0;
    background: var(--bg-page);
  }

  .pricing-card {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 26px;
    overflow: hidden;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  }

  .pricing-badge {
    position: absolute;
    top: 24px;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 100%);
    color: var(--ink);
    border-radius: 20px;
    font-size: 12.5px;
    font-weight: 600;
  }

  .pricing-layout {
    display: grid;
    grid-template-columns: 350px 1fr;
  }

  .pricing-summary {
    background: linear-gradient(165deg, #1A2230 0%, #0C1017 100%);
    padding: 56px 36px;
    text-align: center;
    border-right: 1px solid var(--border-ember);
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .pricing-icon {
    width: 60px;
    height: 60px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 100%);
  }

  .pricing-plan-name {
    font-size: 1.6rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .pricing-tagline {
    font-size: 0.97rem;
    color: var(--text-secondary);
    margin-bottom: 26px;
  }

  .pricing-amount-container {
    margin-bottom: 28px;
  }

  .pricing-amount-large {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 6px;
    font-family: var(--font-display);
    color: var(--ember-light);
  }

  .currency {
    font-size: 2rem;
    font-weight: 600;
    margin-right: 4px;
    margin-top: 8px;
  }

  .amount {
    font-size: 4.5rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.03em;
  }

  .amount .cents {
    font-size: 1.9rem;
    letter-spacing: 0;
    vertical-align: super;
  }

  .pricing-period-label {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 14px;
  }

  .pricing-note {
    font-size: 0.87rem;
    color: var(--text-muted);
    margin: 0;
  }

  .pricing-btn {
    font-size: 16px;
    padding: 28px !important;
  }

  .pricing-guarantees {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 14px;
    border: 1px solid var(--border-subtle);
    width: 100%;
    margin-top: 8px;
  }

  .guarantee-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.88rem;
    color: var(--text-secondary);
    font-weight: 500;
  }

  .pricing-features-panel {
    padding: 56px 40px 44px;
  }

  .feature-groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 30px 36px;
  }

  .group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-subtle);
  }

  .group-header h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .group-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .feature-row {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: default;
  }

  .feature-name {
    font-size: 0.93rem;
    color: var(--text-secondary);
    line-height: 1.3;
  }

  /* ==================== FINAL CTA SECTION ==================== */
  .final-cta-section {
    position: relative;
    padding: 120px 0;
    background: linear-gradient(165deg, #151B25 0%, #0A0E14 60%, #07090D 100%);
    border-top: 1px solid var(--border-subtle);
    overflow: hidden;
  }

  .cta-background {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .cta-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 122, 69, 0.22) 0%, transparent 70%);
    filter: blur(60px);
  }

  .cta-orb-1 {
    width: 400px;
    height: 400px;
    top: -120px;
    left: -100px;
  }

  .cta-orb-2 {
    width: 500px;
    height: 500px;
    bottom: -170px;
    right: -150px;
  }

  .cta-content {
    position: relative;
    z-index: 2;
  }

  .cta-mark {
    width: 68px;
    height: 68px;
    border-radius: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 55%, #D9501E 100%);
    box-shadow: 0 14px 34px rgba(255, 122, 69, 0.28);
  }

  .cta-title {
    font-size: 3.25rem;
    font-weight: 600;
    letter-spacing: -0.022em;
    line-height: 1.1;
    color: var(--text-primary);
  }

  .cta-subtitle {
    font-size: 1.2rem;
    color: var(--text-secondary);
    max-width: 620px;
    margin: 0 auto 40px;
    line-height: 1.6;
  }

  .cta-buttons-final {
    display: flex;
    justify-content: center;
  }

  .cta-btn-final {
    font-size: 16px;
    padding: 26px 44px !important;
  }

  /* ==================== RESPONSIVE STYLES ==================== */
  @media (max-width: 1264px) {
    .hero-title {
      font-size: 3rem;
    }

    .section-title {
      font-size: 2.6rem;
    }
  }

  @media (max-width: 960px) {
    .pricing-layout {
      grid-template-columns: 1fr;
    }

    .pricing-summary {
      border-right: none;
      border-bottom: 1px solid var(--border-ember);
    }

    .hero-section {
      min-height: auto;
      padding: 56px 0 60px;
    }

    .hero-title {
      font-size: 2.6rem;
    }

    .hero-subtitle {
      font-size: 1.1rem;
      margin-left: auto;
      margin-right: auto;
    }

    .cta-buttons,
    .trust-indicators {
      justify-content: center;
    }

    .section-title {
      font-size: 2.15rem;
    }

    .section-subtitle {
      font-size: 1.08rem;
    }

    .features-section,
    .platform-section,
    .benefits-section,
    .pricing-section {
      padding: 64px 0;
    }

    .final-cta-section {
      padding: 80px 0;
    }

    .cta-title {
      font-size: 2.5rem;
    }

    .platform-tab {
      padding: 12px 8px;
    }

    .platform-tab span {
      display: none;
    }
  }

  @media (max-width: 600px) {
    .hero-title {
      font-size: 2.15rem;
    }

    .hero-subtitle {
      font-size: 1.02rem;
    }

    .cta-btn-primary {
      padding: 22px 28px !important;
    }

    .trust-badge {
      font-size: 12px;
      padding: 6px 12px;
    }

    /* Scale the whole phone rather than reflow its fixed-size contents */
    .phone-frame {
      transform: scale(0.84);
      transform-origin: top center;
      margin-bottom: -110px;
    }

    .section-title {
      font-size: 1.85rem;
    }

    .feature-card,
    .benefit-card {
      padding: 28px 24px;
    }

    .platform-content {
      padding: 24px;
    }

    .platform-panel-title {
      font-size: 1.45rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .pricing-summary {
      padding: 64px 24px 40px;
    }

    .pricing-features-panel {
      padding: 32px 24px;
    }

    .feature-groups {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .amount {
      font-size: 3.75rem;
    }

    .cta-title {
      font-size: 2.05rem;
    }

    .cta-subtitle {
      font-size: 1.08rem;
    }
  }

  /* ==================== TRANSITIONS & MOTION PREFERENCES ==================== */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .reveal,
    .hero-eyebrow,
    .hero-title,
    .hero-subtitle,
    .cta-buttons,
    .trust-indicators,
    .phone-mockup-container {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }

    .btn-ember {
      transition: none;
    }
  }

.pricing-onboarding {
  margin: 6px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #FFB07A;
}
  /* ---------- Tribzz ---------- */
.tribzz-section { padding: 40px 0 104px; background: var(--bg-page); }
.tribzz-band {
  display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 56px; align-items: center;
  background: linear-gradient(135deg, var(--bg-card) 0%, var(--bg-alt) 100%);
  border: 1px solid var(--border-subtle); border-radius: 28px; padding: 56px;
}
.tribzz-title { text-align: left; font-size: 2.6rem; }
.tribzz-text { font-size: 1.08rem; line-height: 1.7; color: var(--text-secondary); max-width: 560px; }
.tribzz-points { list-style: none; padding: 0; margin: 22px 0 28px; display: grid; gap: 10px; }
.tribzz-points li { display: flex; gap: 10px; align-items: center; color: var(--text-primary); }
.tribzz-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 22px; }
.tribzz-link { color: var(--ember-light); font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
.tribzz-link:hover { color: var(--ember); }
/* Drawn in Tribzz's own look so gym owners recognise it there */
.tribzz-mock { display: flex; justify-content: center; }
.tm-card { width: 100%; max-width: 340px; background: #F4EEE6; color: #1A1715; border: 1px solid #DCD2C4; box-shadow: 0 30px 70px rgba(0, 0, 0, 0.4); transform: rotate(1.5deg); }
.tm-cover { height: 120px; background: linear-gradient(135deg, #1A1715, #3A2A22); border-bottom: 4px solid #B8442F; display: flex; align-items: center; justify-content: center; }
.tm-cover span { font-family: 'Fraunces', Georgia, serif; font-size: 54px; font-weight: 700; color: #F4EEE6; }
.tm-body { padding: 16px 18px 18px; }
.tm-title { font-family: 'Fraunces', Georgia, serif; font-size: 21px; font-weight: 700; }
.tm-sub { font-family: Georgia, serif; color: #4A423C; font-size: 14px; margin-top: 2px; }
.tm-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 12px; }
.tm-chips span { border: 1px solid #1A1715; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; padding: 3px 7px; font-family: ui-monospace, Menlo, monospace; }
.tm-post { margin-top: 12px; padding-left: 10px; border-left: 2px solid #B8442F; font-family: Georgia, serif; font-style: italic; font-size: 14px; }
.tm-join { margin-top: 14px; background: #1A1715; color: #F4EEE6; text-align: center; padding: 9px; letter-spacing: 0.2em; font-size: 11px; font-weight: 700; }
@media (max-width: 960px) {
  .tribzz-band { grid-template-columns: 1fr; padding: 36px 24px; gap: 36px; }
  .tm-card { transform: none; }
}
</style>